import { memo } from 'react';
import { useHistory } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import BackButtonComponent from '../../Forms/BackButtonComponent';
import { CircularLoaderComponent } from '../../LoadingSpinner/LoadingSpinnerComponent';

import {
  checkCanUserEditEvent,
  formatDateTime,
  formatDateTimeRange,
  getDayOfWeek,
  parseCategoriesList,
} from '../utils';

import ClockIcon from '../../../res/svg/ClockIcon';
import LocationIcon from '../../../res/svg/LocationIcon';
import PersonIcon from '../../../res/svg/PersonIcon';
import EditButton from '../../Shared/components/Buttons/EditButton';
import StyledLink from '../../StyledLink';

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  textTransform: 'capitalize',
}));

const StyledDetailsIcon = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  width: 20,
}));

const StyledContactInfoContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: '2rem',
  gap: '1rem',
}));

const StyledParagraph = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isPrimaryColor',
})(({ isPrimaryColor, theme }) => ({
  padding: 0,
  margin: 0,
  ...(isPrimaryColor && {
    color: theme.palette.primary.main,
  }),
}));

const StyledDescriptionContainer = styled(Box)(({ theme }) => ({
  marginBottom: '3rem',
  [theme.breakpoints.up('lg')]: {
    width: '50%',
  },
}));

const EventDetailsComponent = ({ event, currentUserId }) => {
  const {
    id,
    name,
    categories,
    startsAt,
    duration,
    locationDescription,
    amenity,
    description,
    organizer,
    creator,
    attendeesCount,
  } = event;

  const history = useHistory();

  const url = `/events/${id}`;
  const rsvpsEventPath = `/events/details/${id}/rsvps`;

  const time = duration
    ? formatDateTimeRange(startsAt, duration)
    : formatDateTime(startsAt);
  const dayOfWeek = getDayOfWeek(startsAt);

  const date = `${dayOfWeek}, ${time}`;

  const isEventEditable = checkCanUserEditEvent(
    currentUserId,
    event?.creator?.id,
    event?.startsAt,
    event?.duration,
  );

  if (!event?.id) return <CircularLoaderComponent />;

  return (
    <Box sx={{ wordBreak: 'break-word' }}>
      <StyledTitle variant="h6">{name}</StyledTitle>
      <Typography>{parseCategoriesList(categories)}</Typography>
      <Box mt="3rem" mb="3rem">
        <StyledContactInfoContainer>
          <StyledDetailsIcon>
            <LocationIcon />
          </StyledDetailsIcon>
          <StyledParagraph>
            {locationDescription || amenity?.name}
          </StyledParagraph>
        </StyledContactInfoContainer>

        <StyledContactInfoContainer>
          <StyledDetailsIcon>
            <ClockIcon />
          </StyledDetailsIcon>
          <StyledParagraph>{date}</StyledParagraph>
        </StyledContactInfoContainer>

        <StyledContactInfoContainer>
          <StyledDetailsIcon>
            <PersonIcon width="24" height="24" />
          </StyledDetailsIcon>
          <StyledLink to={rsvpsEventPath}>
            <StyledParagraph isPrimaryColor>
              {attendeesCount} going
            </StyledParagraph>
          </StyledLink>
        </StyledContactInfoContainer>
      </Box>
      <Box>
        <StyledDescriptionContainer>
          <Typography fontWeight="bold">About this event</Typography>
          <StyledParagraph>
            {description || 'Description is missing'}
          </StyledParagraph>
        </StyledDescriptionContainer>
        <Box>
          <Typography fontWeight="bold">Organizer</Typography>
          <StyledParagraph>{organizer || creator?.fullName}</StyledParagraph>
        </Box>
      </Box>
      <Box mt="3rem" display="flex" gap="1rem">
        <BackButtonComponent backUrl="/events/details" width="13rem" />
        {isEventEditable && (
          <EditButton text="Edit event" handleClick={() => history.push(url)} />
        )}
      </Box>
    </Box>
  );
};

EventDetailsComponent.propTypes = {
  event: PropTypes.instanceOf(Object).isRequired,
  currentUserId: PropTypes.number,
};

export default memo(EventDetailsComponent);
